import type {Dispatch, ThunkAction} from 'src/reducers';
import type {
  CreateTaskPayload,
  CreatedTaskResponse,
  TaskDetailsResponse,
  TaskListParams,
  TaskListResponse,
  SnoozePayload,
  TaskTypeOption,
  UpdateTaskPayload,
  TaskTab,
} from 'src/types/task-and-reminder';

import * as reduxApi from 'src/utils/redux-api-v2';
import {taskListParamsFromQuery} from 'src/utils/task-and-reminder';
import moment from 'moment-timezone';


export const GET_TASK_DETAILS_LOADING = 'get-task-details-loading';
export const GET_TASK_DETAILS = 'get-task-details';
export const RESET_TASK_DETAILS_ACTION = 'reset-task-details-action';
export const GET_TASK_DETAILS_ERROR = 'get-task-details-error';
export const FETCH_TASKS_LOADING = 'fetch-tasks-loading';
export const FETCH_TASKS_SUCCESS = 'fetch-tasks-success';
export const FETCH_TASK_TYPES = 'fetch-task-types';
export const UPSERT_TASK_IN_TASK_LIST = 'upsert-task-in-task-list';
export const REMOVE_TASK_FROM_TASK_LIST = 'remove-task-from-task-list';

type GetTaskDetailsLoadingAction = {
  type: typeof GET_TASK_DETAILS_LOADING;
};

type GetTaskDetailsSuccessAction = {
  type: typeof GET_TASK_DETAILS;
  payload: TaskDetailsResponse;
};

type ResetTaskDetailsActionType = {
  type: typeof RESET_TASK_DETAILS_ACTION;
};

type GetTaskDetailsErrorAction = {
  type: typeof GET_TASK_DETAILS_ERROR;
  payload: unknown;
};

type FetchTasksLoadingAction = {
  type: typeof FETCH_TASKS_LOADING;
  payload: {append: boolean};
};

type FetchTasksSuccessAction = {
  type: typeof FETCH_TASKS_SUCCESS;
  payload: TaskListResponse;
  meta: {append: boolean};
};

type FetchTaskTypesAction = {
  type: typeof FETCH_TASK_TYPES;
  payload: Array<TaskTypeOption>;
};

type updateTaskInTaskListAction = {
  type: typeof UPSERT_TASK_IN_TASK_LIST;
  payload: TaskDetailsResponse;
};

type RemoveTaskFromTaskListAction = {
  type: typeof REMOVE_TASK_FROM_TASK_LIST;
  payload: {taskId: number | string};
};

export type TaskAndReminderAction =
  | GetTaskDetailsLoadingAction
  | GetTaskDetailsSuccessAction
  | GetTaskDetailsErrorAction
  | FetchTasksLoadingAction
  | FetchTasksSuccessAction
  | FetchTaskTypesAction
  | ResetTaskDetailsActionType
  | updateTaskInTaskListAction
  | RemoveTaskFromTaskListAction;

const getTaskDetailsLoading = (): GetTaskDetailsLoadingAction => ({
  type: GET_TASK_DETAILS_LOADING,
});

const getTaskDetailsSuccess = (
  payload: TaskDetailsResponse,
): GetTaskDetailsSuccessAction => ({
  type: GET_TASK_DETAILS,
  payload,
});

export const resetTaskDetails = (): ResetTaskDetailsActionType => ({
  type: RESET_TASK_DETAILS_ACTION,
});

const getTaskDetailsError = (payload: unknown): GetTaskDetailsErrorAction => ({
  type: GET_TASK_DETAILS_ERROR,
  payload,
});

const fetchTasksLoading = (append: boolean): FetchTasksLoadingAction => ({
  type: FETCH_TASKS_LOADING,
  payload: {append},
});

const fetchTasksSuccess = (
  payload: TaskListResponse,
  append: boolean,
): FetchTasksSuccessAction => ({
  type: FETCH_TASKS_SUCCESS,
  payload,
  meta: {append},
});

const fetchTaskTypesReceive = (
  payload: Array<TaskTypeOption>,
): FetchTaskTypesAction => ({
  type: FETCH_TASK_TYPES,
  payload,
});

const updateTaskInTaskList = (
  task: TaskDetailsResponse,
): updateTaskInTaskListAction => ({
  type: UPSERT_TASK_IN_TASK_LIST,
  payload: task,
});

const removeTaskFromTaskList = (
  taskId: number | string,
): RemoveTaskFromTaskListAction => ({
  type: REMOVE_TASK_FROM_TASK_LIST,
  payload: {taskId},
});

export const fetchTaskTypes: () => ThunkAction<Array<TaskTypeOption>> =
  () => (dispatch: Dispatch) =>
    dispatch(reduxApi.get('tasks/types', {})).then((response) => {
      const list = Array.isArray(response)
        ? (response as Array<TaskTypeOption>)
        : [];
      dispatch(fetchTaskTypesReceive(list));
      return list;
    });

export type FetchTasksOptions = {
  append?: boolean;
};

export const fetchTasks =
  (
    params: TaskListParams,
    options?: FetchTasksOptions,
  ): ThunkAction<TaskListResponse> =>
  async (dispatch: Dispatch) => {
    const append = options?.append ?? false;
    dispatch(fetchTasksLoading(append));
    try {
      const payload = Object.fromEntries(
        Object.entries(params).filter(([_, value]) => value !== null),
      );
      const response = await dispatch(reduxApi.post('tasks/list', payload, {}));
      const result = (response ?? {
        total_records: 0,
        data: [],
      }) as TaskListResponse;
      dispatch(fetchTasksSuccess(result, append));
      return result;
    } catch (error: any) {
      return error;
    }
  };

export const getTaskDetails =
  (taskId: number | string): ThunkAction<TaskDetailsResponse> =>
  async (dispatch: Dispatch) => {
    dispatch(getTaskDetailsLoading());
    try {
      const response = await dispatch(reduxApi.get(`tasks/${taskId}`, {}));
      const taskDetails = (response || {}) as TaskDetailsResponse;
      dispatch(getTaskDetailsSuccess(taskDetails));
      return taskDetails;
    } catch (error: any) {
      return error;
    }
  };

export const addTask =
  (
    payload: CreateTaskPayload,
    pageQuery?: Record<string, unknown>,
  ): ThunkAction<CreatedTaskResponse> =>
  async (dispatch: Dispatch) => {
    try {
      const response = await dispatch(reduxApi.post('tasks', payload, {}));
      await dispatch(fetchTasks(taskListParamsFromQuery(pageQuery ?? {})));
      return (response || {}) as CreatedTaskResponse;
    } catch (error: any) {
      const errorPayload =
        error?.response?.errors || error?.response?.message || error;
      throw errorPayload;
    }
  };

export const deleteTask =
  (taskId: number | string): ThunkAction<void> =>
  async (dispatch: Dispatch) => {
    await dispatch(reduxApi.del(`tasks/${taskId}`, {}));
    dispatch(removeTaskFromTaskList(taskId));
  };

export const updateTask =
  (
    payload: UpdateTaskPayload,
    taskId: number | string,
    tab?: TaskTab,
  ): ThunkAction<TaskDetailsResponse> =>
  async (dispatch: Dispatch) => {
    try {
      const response = await dispatch(
        reduxApi.put(`tasks/${taskId}`, payload, {}),
      );
      const taskDetails = (response || {}) as TaskDetailsResponse;
      dispatch(getTaskDetailsSuccess(taskDetails));

      const taskCompleted = taskDetails.status === 'completed';
      const isNoLongerOverdue =
        tab === 'overdue' &&
        moment.utc(taskDetails.time_due).isValid() &&
        moment().isBefore(moment.utc(taskDetails.time_due), 'minute');

      if (taskCompleted || isNoLongerOverdue) {
        dispatch(removeTaskFromTaskList(taskId));
      } else {
        dispatch(updateTaskInTaskList(taskDetails));
      }
      return taskDetails;
    } catch (error: any) {
      const errorPayload =
        error?.response?.errors || error?.response?.message || error;
      dispatch(getTaskDetailsError(errorPayload));
      throw errorPayload;
    }
  };

export const snoozeTask =
  (
    taskId: number,
    payload: SnoozePayload,
    tab: TaskTab,
  ): ThunkAction<TaskDetailsResponse> =>
  async (dispatch: Dispatch) => {
    const response = await dispatch(
      reduxApi.put(`tasks/${taskId}`, payload, {}),
    );
    const taskDetails = (response || {}) as TaskDetailsResponse;
    const now = moment();
    const dueMoment = moment.utc(taskDetails.time_due);
    const hasValidDue = dueMoment.isValid();
    const isNowAfterDue = hasValidDue && now.isAfter(dueMoment, 'minute');

    if (tab === 'upcoming' && hasValidDue && !isNowAfterDue) {
      dispatch(updateTaskInTaskList(taskDetails));
    } else {
      dispatch(removeTaskFromTaskList(taskId));
    }
    return taskDetails;
  };

export const completeTask =
  (taskId: number): ThunkAction<TaskDetailsResponse> =>
  async (dispatch: Dispatch) => {
    try {
      const response = await dispatch(
        reduxApi.put(`tasks/${taskId}`, {status: 'completed'}, {}),
      );
      dispatch(removeTaskFromTaskList(taskId));
      return (response || {}) as TaskDetailsResponse;
    } catch (error: any) {
      const errorPayload =
        error?.response?.errors || error?.response?.message || error;
      throw errorPayload;
    }
  };

export const undoCompleteTask =
  (taskId: number): ThunkAction<TaskDetailsResponse> =>
  async (dispatch: Dispatch) => {
    const response = await dispatch(
      reduxApi.put(`tasks/${taskId}`, {status: 'created'}, {}),
    );
    dispatch(removeTaskFromTaskList(taskId));
    return (response || {}) as TaskDetailsResponse;
  };
